import * as THREE from 'three';
import { Project, BIMElement, WallElement } from './types';
import { WallJoinSolver } from './WallJoinSolver';

export class ConstraintSolver {
    /**
     * Solve constraints after an element has been modified.
     * Returns a map of updates to be applied to other elements.
     */
    static solve(
        project: Project,
        modifiedElementId: string,
        oldElementState?: BIMElement
    ): Map<string, Partial<BIMElement>> {
        const updates = new Map<string, Partial<BIMElement>>();

        // 1. Handle Level Changes
        const modifiedLevel = project.levels.get(modifiedElementId);
        if (modifiedLevel) {
            project.elements.forEach(el => {
                if (el.category === 'Walls') {
                    const wall = el as WallElement;
                    let changed = false;
                    let newHeight = wall.geometryData.height;

                    // Check Top Constraint
                    if (wall.geometryData.topConstraintId === modifiedElementId ||
                        wall.geometryData.baseConstraintId === modifiedElementId) {

                        const baseLevel = project.levels.get(wall.geometryData.baseConstraintId);
                        const topLevel = project.levels.get(wall.geometryData.topConstraintId);

                        if (baseLevel && topLevel) {
                            // Calculate new height
                            const h = (topLevel.elevation + (wall.geometryData.topOffset * 1000)) -
                                (baseLevel.elevation + (wall.geometryData.baseOffset * 1000));
                            newHeight = h / 1000;
                            changed = true;
                        }
                    }

                    if (changed) {
                        updates.set(wall.id, {
                            geometryData: {
                                ...wall.geometryData,
                                height: newHeight
                            }
                        });
                    }
                }
            });
            return updates;
        }

        const modifiedElement = project.elements.get(modifiedElementId);
        if (!modifiedElement) return updates;

        // 2. Handle Hosted Elements (If modified element is a Host)
        // Find elements that have this element as a host
        const hostedElements = Array.from(project.elements.values()).filter(e => {
            // Check geometryData.hostWallId or constraints
            const geom = (e as any).geometryData;
            return geom && geom.hostWallId === modifiedElementId;
        });

        if (hostedElements.length > 0 && oldElementState) {
            // Calculate delta move
            const oldStart = new THREE.Vector3(...(oldElementState as any).geometryData.start);
            const newStart = new THREE.Vector3(...(modifiedElement as any).geometryData.start);
            const delta = newStart.sub(oldStart);

            if (delta.lengthSq() > 0.0001) {
                // Implicitly handled by parametric definition (position 0-1)
            }
        }

        // 3. Handle Wall Joins (If modified element is a Wall)
        if (modifiedElement.category === 'Walls') {
            const wall = modifiedElement as WallElement;

            // Find connected walls
            const existingWalls = Array.from(project.elements.values())
                .filter(e => e.category === 'Walls' && e.id !== wall.id) as WallElement[];

            const connectedIds = wall.constraints
                .filter(c => c.type === 'JOIN')
                .map(c => c.targetId);

            connectedIds.forEach(id => {
                const connectedWall = project.elements.get(id) as WallElement;
                if (connectedWall) {
                    // Re-solve profile for connected wall
                    const itsConnections = existingWalls
                        .filter(w => w.id !== id) // all except itself
                        .concat([wall]); // include modified wall

                    // Filter to actual touching ones
                    const touching = itsConnections.filter(w => ConstraintSolver.areWallsTouching(connectedWall, w));

                    const newProfile = WallJoinSolver.computeWallProfile(connectedWall, touching);

                    updates.set(id, {
                        geometryData: {
                            ...connectedWall.geometryData,
                            profile: newProfile
                        }
                    });
                }
            });

            // Also update the modified wall itself
            const touchingModified = existingWalls.filter(w => ConstraintSolver.areWallsTouching(wall, w));
            const myNewProfile = WallJoinSolver.computeWallProfile(wall, touchingModified);
            updates.set(wall.id, {
                geometryData: {
                    ...wall.geometryData,
                    profile: myNewProfile
                }
            });
        }

        return updates;
    }

    private static areWallsTouching(w1: WallElement, w2: WallElement): boolean {
        const start1 = new THREE.Vector3(...w1.geometryData.start);
        const end1 = new THREE.Vector3(...w1.geometryData.end);
        const start2 = new THREE.Vector3(...w2.geometryData.start);
        const end2 = new THREE.Vector3(...w2.geometryData.end);
        const tol = 0.1; // 10cm tolerance

        return start1.distanceTo(start2) < tol || start1.distanceTo(end2) < tol ||
            end1.distanceTo(start2) < tol || end1.distanceTo(end2) < tol;
    }

    // ===========================================
    // ADVANCED CONSTRAINT SOLVING (Phase 2.2)
    // ===========================================

    /**
     * Propagate movement from a modified element to its constrained followers.
     */
    static solveAlignment(
        project: Project,
        modifiedElement: BIMElement,
        delta: THREE.Vector3
    ): Map<string, Partial<BIMElement>> {
        const updates = new Map<string, Partial<BIMElement>>();

        project.elements.forEach(follower => {
            if (follower.id === modifiedElement.id) return;

            const alignment = follower.constraints.find(c => c.type === 'ALIGNED' && c.targetId === modifiedElement.id);
            if (alignment) {
                const currentStart = new THREE.Vector3(...(follower as any).geometryData.start);
                const currentEnd = new THREE.Vector3(...(follower as any).geometryData.end);

                const newStart = currentStart.add(delta);
                const newEnd = currentEnd.add(delta);

                updates.set(follower.id, {
                    geometryData: {
                        ...(follower as any).geometryData,
                        start: [newStart.x, newStart.y, newStart.z],
                        end: [newEnd.x, newEnd.y, newEnd.z]
                    }
                });
            }
        });
        return updates;
    }

    /**
     * Solve locked dimensions (Spacing constraints).
     */
    static solveLockedDimensions(
        project: Project,
        modifiedElement: BIMElement,
        delta: THREE.Vector3
    ): Map<string, Partial<BIMElement>> {
        const updates = new Map<string, Partial<BIMElement>>();

        project.elements.forEach(el => {
            if (el.id === modifiedElement.id) return;

            const lock = el.constraints.find(c => c.type === 'LOCKED' && c.targetId === modifiedElement.id);
            if (lock) {
                const currentStart = new THREE.Vector3(...(el as any).geometryData.start);
                const currentEnd = new THREE.Vector3(...(el as any).geometryData.end);

                const newStart = currentStart.add(delta);
                const newEnd = currentEnd.add(delta);

                updates.set(el.id, {
                    geometryData: {
                        ...(el as any).geometryData,
                        start: [newStart.x, newStart.y, newStart.z],
                        end: [newEnd.x, newEnd.y, newEnd.z]
                    }
                });
            }
        });
        return updates;
    }
}
